import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaDollarSign, FaHome, FaChartLine, FaClipboardList } from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import StatCard from '../components/StatCard';
import PageLoader from '../components/PageLoader';
import { apiJson, isUnauthorizedError } from '../services/api';
import './Dashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

const PROPERTY_SERIES = [
  { name: 'Euclid', border: '#58b6ff', background: 'rgba(88, 182, 255, 0.12)' },
  { name: 'Shich', border: '#f2c14e', background: 'rgba(242, 193, 78, 0.12)' },
  { name: 'Ritter', border: '#ef6f6c', background: 'rgba(239, 111, 108, 0.12)' },
  { name: 'Roselawn', border: '#63d6a4', background: 'rgba(99, 214, 164, 0.12)' },
  { name: 'Midlothian', border: '#b58cff', background: 'rgba(181, 140, 255, 0.12)' },
  { name: 'Unknown', border: '#c8d0d6', background: 'rgba(200, 208, 214, 0.12)' },
];

const FALLBACK_SERIES = [
  { border: '#7dd3fc', background: 'rgba(125, 211, 252, 0.12)' },
  { border: '#fca5a5', background: 'rgba(252, 165, 165, 0.12)' },
  { border: '#86efac', background: 'rgba(134, 239, 172, 0.12)' },
  { border: '#fde68a', background: 'rgba(253, 230, 138, 0.12)' },
];

const fmtCurrency = (value) =>
  value == null ? '—' : `$${Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

const fmtCompactCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
    notation: Math.abs(amount) >= 100000 ? 'compact' : 'standard',
  }).format(amount);
};

const formatPeriodLabel = (period) => {
  if (!period) return '—';
  const date = new Date(`${period}-02T00:00:00`);
  return Number.isNaN(date.getTime())
    ? period
    : date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const numeric = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const countLabel = (count, singular, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

const toYearLabel = (value) => {
  if (!value) return 'Unknown';
  const raw = String(value);
  const directMatch = raw.match(/^\d{4}/);
  if (directMatch) return directMatch[0];

  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? raw : String(date.getFullYear());
};

const getPropertySeries = (property, index) =>
  PROPERTY_SERIES.find((series) => series.name === property) || FALLBACK_SERIES[index % FALLBACK_SERIES.length];

function buildAnnualNetIncomeChart(rows, field = 'net_income') {
  const valuesByYear = new Map();
  const properties = new Set();

  rows.forEach((row) => {
    const year = toYearLabel(row.year);
    const property = row.property || 'Unknown';
    properties.add(property);

    if (!valuesByYear.has(year)) {
      valuesByYear.set(year, new Map());
    }
    valuesByYear.get(year).set(property, numeric(row[field] ?? row.net_income));
  });

  const labels = [...valuesByYear.keys()].sort((left, right) => Number(left) - Number(right));
  const orderedProperties = [
    ...PROPERTY_SERIES.map((series) => series.name).filter((property) => properties.has(property)),
    ...[...properties].filter((property) => !PROPERTY_SERIES.some((series) => series.name === property)).sort(),
  ];

  return {
    labels,
    datasets: orderedProperties.map((property, index) => {
      const series = getPropertySeries(property, index);
      return {
        label: property,
        data: labels.map((year) => valuesByYear.get(year)?.get(property) ?? null),
        borderColor: series.border,
        backgroundColor: series.background,
        borderWidth: 3,
        pointBackgroundColor: '#101815',
        pointBorderColor: series.border,
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.36,
        fill: false,
        spanGaps: true,
      };
    }),
  };
}

function getAnnualYearOptions(rows) {
  return [...new Set(rows.map((row) => toYearLabel(row.year)).filter((year) => year !== 'Unknown'))]
    .sort((left, right) => Number(left) - Number(right));
}

function chartToAnnualRows(chart) {
  if (!chart?.labels || !chart?.datasets) return [];

  return chart.datasets.flatMap((dataset) =>
    chart.labels.map((year, index) => ({
      year,
      property: dataset.label,
      net_income: dataset.data[index],
    })).filter((row) => row.net_income !== null && row.net_income !== undefined)
  );
}

function filterRowsByYearRange(rows, range) {
  if (!range.start || !range.end) return rows;

  return rows.filter((row) => {
    const year = Number(toYearLabel(row.year));
    return year >= Number(range.start) && year <= Number(range.end);
  });
}

function completeAnnualRows(rows, years) {
  if (!rows.length || !years.length) return [];

  const properties = new Map();
  const values = new Map();
  const firstYear = new Map();

  rows.forEach((row) => {
    const propertyKey = row.property_id != null
      ? `property:${row.property_id}`
      : `property:${row.property || 'Unknown'}`;
    const property = row.property || 'Unknown';
    const year = toYearLabel(row.year);

    properties.set(propertyKey, { property_id: row.property_id, property });
    // store both income fields so the debt toggle works correctly
    values.set(`${propertyKey}:${year}`, {
      net_income: row.net_income != null ? numeric(row.net_income) : null,
      net_income_after_debt: row.net_income_after_debt != null ? numeric(row.net_income_after_debt) : null,
    });

    // track the earliest year this property has any data
    if (!firstYear.has(propertyKey) || year < firstYear.get(propertyKey)) {
      firstYear.set(propertyKey, year);
    }
  });

  return [...properties.entries()].flatMap(([propertyKey, propertyInfo]) =>
    years.map((year) => {
      // skip years before the property's first data point
      if (year < firstYear.get(propertyKey)) return null;
      const v = values.get(`${propertyKey}:${year}`);
      return {
        ...propertyInfo,
        year,
        net_income: v?.net_income ?? null,
        net_income_after_debt: v?.net_income_after_debt ?? null,
      };
    }).filter(Boolean)
  );
}

function getPropertyTotals(rows, field = 'net_income') {
  const totals = new Map();

  rows.forEach((row) => {
    const property = row.property || 'Unknown';
    const val = row[field] != null ? numeric(row[field]) : numeric(row.net_income);
    totals.set(property, (totals.get(property) || 0) + val);
  });

  return [...totals.entries()]
    .map(([property, total]) => ({ property, total }))
    .sort((left, right) => right.total - left.total);
}

function getConsistencyLeader(rows, field = 'net_income') {
  const valuesByProperty = new Map();

  rows.forEach((row) => {
    const property = row.property || 'Unknown';
    if (!valuesByProperty.has(property)) {
      valuesByProperty.set(property, []);
    }
    const val = row[field] != null ? numeric(row[field]) : numeric(row.net_income);
    valuesByProperty.get(property).push(val);
  });

  const candidates = [...valuesByProperty.entries()]
    .filter(([, values]) => values.length >= 2)
    .map(([property, values]) => {
      const average = values.reduce((sum, value) => sum + value, 0) / values.length;
      const variance = values.reduce((sum, value) => sum + ((value - average) ** 2), 0) / values.length;
      const volatility = Math.sqrt(variance);
      const positiveYears = values.filter((value) => value > 0).length;
      const positiveRate = positiveYears / values.length;
      const reliabilityScore = (average * positiveRate) - volatility;

      return {
        property,
        average,
        volatility,
        positiveYears,
        years: values.length,
        positiveRate,
        reliabilityScore,
      };
    })
    .sort((left, right) => {
      if (right.reliabilityScore !== left.reliabilityScore) return right.reliabilityScore - left.reliabilityScore;
      if (right.positiveRate !== left.positiveRate) return right.positiveRate - left.positiveRate;
      return right.average - left.average;
    });

  if (!candidates.length) {
    return {
      property: '—',
      detail: 'Need 2+ years to compare',
    };
  }

  const leader = candidates[0];
  return {
    property: leader.property,
    detail: `${fmtCompactCurrency(leader.average)} avg/yr`,
  };
}

function buildAnnualNetIncomeMeta(rows, timeframeLabel = 'All years', field = 'net_income') {
  if (!rows.length) {
    return {
      timeframeLabel,
      topEarner: '—',
      topEarnerTotal: '—',
      mostConsistent: '—',
      consistencyDetail: 'No comparison data',
      totalTracked: '—',
      coverageLabel: 'No annual data',
    };
  }

  const propertyTotals = getPropertyTotals(rows, field);
  const topEarner = propertyTotals[0];
  const consistencyLeader = getConsistencyLeader(rows, field);
  const totalTracked = rows.reduce((sum, row) => {
    const val = row[field] != null ? numeric(row[field]) : numeric(row.net_income);
    return sum + val;
  }, 0);
  const propertyCount = new Set(rows.map((row) => row.property || 'Unknown')).size;
  const yearCount = new Set(rows.map((row) => toYearLabel(row.year))).size;

  return {
    timeframeLabel,
    topEarner: topEarner?.property || '—',
    topEarnerTotal: fmtCurrency(topEarner?.total),
    mostConsistent: consistencyLeader.property,
    consistencyDetail: consistencyLeader.detail,
    totalTracked: fmtCurrency(totalTracked),
    coverageLabel: `${countLabel(propertyCount, 'property', 'properties')} · ${countLabel(yearCount, 'year')}`,
  };
}

function calcTotalDepreciation(properties) {
  const today = new Date();
  let total = 0;
  properties.forEach((p) => {
    const price = numeric(p.purchase_price);
    if (!price || !p.purchase_date) return;
    const purchased = new Date(p.purchase_date);
    if (isNaN(purchased.getTime())) return;
    // IRS: residential property depreciates over 27.5 years on ~80% of value (building only)
    const buildingValue = price * 0.8;
    const annualDepr = buildingValue / 27.5;
    const yearsHeld = Math.max(0, (today - purchased) / (1000 * 60 * 60 * 24 * 365.25));
    total += Math.min(annualDepr * yearsHeld, buildingValue);
  });
  return total;
}

function buildDashboardData(properties, monthlySummaries, annualNetIncomeRows) {
  const propertyValues = properties.map((p) => numeric(p.current_value || p.purchase_price));
  const portfolioValue = propertyValues.reduce((sum, v) => sum + v, 0);
  const latestSummary = monthlySummaries[0] || null;
  const annualRows = Array.isArray(annualNetIncomeRows) ? annualNetIncomeRows : [];

  // Total equity growth: sum of (current_value - purchase_price) for all properties
  const totalEquityGrowth = properties.reduce((sum, p) => {
    const current = numeric(p.current_value);
    const purchased = numeric(p.purchase_price);
    if (!current || !purchased) return sum;
    return sum + (current - purchased);
  }, 0);

  // Total net profit across all years from annual rows
  const totalNetProfit = annualRows.reduce((sum, row) => sum + numeric(row.net_income), 0);

  // Total accumulated depreciation (tax savings basis)
  const totalDepreciation = calcTotalDepreciation(properties);

  return {
    stats: {
      portfolioValue: fmtCurrency(portfolioValue),
      totalProperties: properties.length.toLocaleString('en-US'),
      totalEquityGrowth: fmtCurrency(totalEquityGrowth),
      totalNetProfit: fmtCurrency(totalNetProfit),
      totalDepreciation: fmtCurrency(totalDepreciation),
    },
    latestPeriodLabel: latestSummary ? formatPeriodLabel(latestSummary.period) : 'No statements yet',
    annualRows,
    annualNetIncomeChart: buildAnnualNetIncomeChart(annualRows),
    annualNetIncomeMeta: buildAnnualNetIncomeMeta(annualRows),
  };
}

const LIGHT_CHART_THEMES = new Set(['light', 'sky', 'white']);

function buildAnnualNetIncomeOptions(isLightTheme = false) {
  const legendColor = isLightTheme ? '#334155' : '#dbe7df';
  const axisColor = isLightTheme ? '#64748b' : '#c7d5cf';
  const borderColor = isLightTheme ? 'rgba(100, 116, 139, 0.24)' : 'rgba(219, 231, 223, 0.24)';
  const xGridColor = isLightTheme ? 'rgba(100, 116, 139, 0.14)' : 'rgba(219, 231, 223, 0.09)';
  const yGridColor = isLightTheme ? 'rgba(100, 116, 139, 0.18)' : 'rgba(219, 231, 223, 0.12)';

  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          color: legendColor,
          boxHeight: 8,
          boxWidth: 8,
          padding: 18,
          usePointStyle: true,
          font: {
            size: 12,
            weight: '600',
          },
        },
      },
      tooltip: {
        backgroundColor: '#f8faf7',
        borderColor: 'rgba(16, 24, 21, 0.16)',
        borderWidth: 1,
        bodyColor: '#101815',
        titleColor: '#101815',
        itemSort: (a, b) => b.parsed.y - a.parsed.y,
        callbacks: {
          label: (context) => `${context.dataset.label}: ${fmtCurrency(context.parsed.y)}`,
        },
      },
    },
    scales: {
      x: {
        border: {
          color: borderColor,
        },
        grid: {
          color: xGridColor,
        },
        ticks: {
          color: axisColor,
          font: {
            size: 12,
            weight: '600',
          },
        },
      },
      y: {
        border: {
          color: borderColor,
        },
        grid: {
          color: yGridColor,
        },
        ticks: {
          color: axisColor,
          callback: (value) => fmtCompactCurrency(value),
          font: {
            size: 12,
            weight: '600',
          },
        },
      },
    },
  };
}

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [yearRangeIndexes, setYearRangeIndexes] = useState({ start: 0, end: null });
  const [companyName, setCompanyName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [chartTheme, setChartTheme] = useState('navy');
  const [showAfterDebt, setShowAfterDebt] = useState(false);

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [properties, monthlySummaries, annualNetIncomeRows, userProfile] = await Promise.all([
        apiJson('/api/properties'),
        apiJson('/api/statements/monthly-summary'),
        apiJson('/api/dashboard/annual-net-income-by-property'),
        apiJson('/api/user'),
      ]);
      setDashboardData(buildDashboardData(
        Array.isArray(properties) ? properties : [],
        Array.isArray(monthlySummaries) ? monthlySummaries : [],
        Array.isArray(annualNetIncomeRows) ? annualNetIncomeRows : []
      ));
      const name = userProfile?.company_name || '';
      setCompanyName(name);
      setNameDraft(name);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      if (isUnauthorizedError(error)) {
        navigate('/login', { replace: true });
        return;
      }
      setError('Unable to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const saveCompanyName = async () => {
    try {
      await apiJson('/api/user', { method: 'PATCH', body: JSON.stringify({ company_name: nameDraft }) });
      setCompanyName(nameDraft);
      setEditingName(false);
    } catch {
      // silently revert
      setNameDraft(companyName);
      setEditingName(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  if (loading) {
    return (
      <PageLoader label="Loading dashboard" />
    );
  }

  if (error || !dashboardData) {
    return (
      <div className="dashboard">
        <div className="dashboard-shell">
          <div className="dashboard-empty-state">{error || 'No dashboard data available'}</div>
        </div>
      </div>
    );
  }

  const annualNetIncomeChart = dashboardData.annualNetIncomeChart
    || dashboardData.annualChart
    || buildAnnualNetIncomeChart([]);
  const annualRows = dashboardData.annualRows?.length
    ? dashboardData.annualRows
    : chartToAnnualRows(annualNetIncomeChart);
  const yearOptions = getAnnualYearOptions(annualRows);
  const maxYearIndex = Math.max(0, yearOptions.length - 1);
  const minYearSpan = yearOptions.length > 1 ? 1 : 0;
  let activeStartYearIndex = Math.min(yearRangeIndexes.start, maxYearIndex);
  let activeEndYearIndex = yearRangeIndexes.end == null
    ? maxYearIndex
    : Math.min(yearRangeIndexes.end, maxYearIndex);

  if (activeEndYearIndex - activeStartYearIndex < minYearSpan) {
    if (activeStartYearIndex + minYearSpan <= maxYearIndex) {
      activeEndYearIndex = activeStartYearIndex + minYearSpan;
    } else {
      activeStartYearIndex = Math.max(0, activeEndYearIndex - minYearSpan);
    }
  }

  const startYear = yearOptions[activeStartYearIndex] || null;
  const endYear = yearOptions[activeEndYearIndex] || null;
  const yearRangeLabel = startYear && endYear
    ? (activeStartYearIndex === 0 && activeEndYearIndex === maxYearIndex ? 'All years' : `${startYear}-${endYear}`)
    : 'All years';
  const rangeStartPercent = maxYearIndex > 0 ? (activeStartYearIndex / maxYearIndex) * 100 : 0;
  const rangeEndPercent = maxYearIndex > 0 ? (activeEndYearIndex / maxYearIndex) * 100 : 100;

  const handleStartYearChange = (event) => {
    const nextStart = Number(event.target.value);
    setYearRangeIndexes((current) => {
      const currentEnd = current.end == null ? maxYearIndex : Math.min(current.end, maxYearIndex);
      return {
        start: Math.min(nextStart, Math.max(0, currentEnd - minYearSpan)),
        end: currentEnd,
      };
    });
  };

  const handleEndYearChange = (event) => {
    const nextEnd = Number(event.target.value);
    setYearRangeIndexes((current) => {
      const currentStart = Math.min(current.start, maxYearIndex);
      return {
        start: currentStart,
        end: Math.max(nextEnd, Math.min(maxYearIndex, currentStart + minYearSpan)),
      };
    });
  };

  const filteredAnnualRows = filterRowsByYearRange(annualRows, {
    start: startYear,
    end: endYear,
  });
  const selectedYears = yearOptions.slice(activeStartYearIndex, activeEndYearIndex + 1);
  const completedAnnualRows = completeAnnualRows(filteredAnnualRows, selectedYears);
  const chartField = showAfterDebt ? 'net_income_after_debt' : 'net_income';

  const filteredAnnualNetIncomeChart = buildAnnualNetIncomeChart(completedAnnualRows, chartField);
  const annualNetIncomeOptions = buildAnnualNetIncomeOptions(LIGHT_CHART_THEMES.has(chartTheme));
  const annualNetIncomeMeta = buildAnnualNetIncomeMeta(completedAnnualRows, yearRangeLabel, chartField);

  // Total net profit stat card — always uses ALL years, respects debt toggle
  const totalNetProfit = fmtCurrency(
    annualRows.reduce((sum, row) => {
      const val = row[chartField] != null ? numeric(row[chartField]) : numeric(row.net_income);
      return sum + val;
    }, 0)
  );

  const hasAnnualNetIncome = filteredAnnualNetIncomeChart.labels.length >= 2
    && filteredAnnualNetIncomeChart.datasets.some((dataset) => dataset.data.some((value) => value !== null));

  return (
    <div className="dashboard">
      <div className="dashboard-shell">
        <header className="dashboard-header">
          <div className="dashboard-heading">
            <p className="eyebrow">Portfolio Analytics
            </p>
            {editingName ? (
              <div className="dashboard-name-edit">
                <input
                  className="dashboard-name-input"
                  value={nameDraft}
                  onChange={e => setNameDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveCompanyName(); if (e.key === 'Escape') { setNameDraft(companyName); setEditingName(false); } }}
                  autoFocus
                  placeholder="Your company name"
                />
                <button className="dashboard-name-save" onClick={saveCompanyName}>Save</button>
                <button className="dashboard-name-cancel" onClick={() => { setNameDraft(companyName); setEditingName(false); }}>✕</button>
              </div>
            ) : (
              <h1 className="dashboard-title" onClick={() => setEditingName(true)} title="Click to edit">
                {companyName || 'Analytics Dashboard'}
                <i className="pi pi-pencil dashboard-title-edit-icon"></i>
              </h1>
            )}
          </div>
          <div className="header-actions">
            <button className="primary-button" onClick={() => navigate('/documents')}>Open Documents</button>
          </div>
        </header>

        <section className="stats-grid">
          <StatCard
            title="Portfolio Value"
            value={dashboardData.stats.portfolioValue}
            icon={FaDollarSign}
            color="#159a74"
          />
          <StatCard
            title="Total Properties"
            value={dashboardData.stats.totalProperties}
            icon={FaHome}
            color="#3178c6"
          />
          <StatCard
            title="Total Equity Growth"
            value={dashboardData.stats.totalEquityGrowth}
            icon={FaChartLine}
            color="#8b5cf6"
          />
          <StatCard
            title={showAfterDebt ? 'Total Net Profit (After Debt)' : 'Total Net Profit'}
            value={totalNetProfit}
            // sub={yearRangeLabel}
            icon={FaClipboardList}
            color="#d97706"
          />
          <StatCard
            title="Total Depreciation"
            value={dashboardData.stats.totalDepreciation}
            icon={FaDollarSign}
            color="#F28B76"
          />
        </section>

        <section className={`annual-chart-section annual-chart-section--${chartTheme}`}>
          <div className="annual-chart-inner">
            <div className="annual-chart-header">
              <div>
                <p className="eyebrow">Portfolio Cash Flow</p>
                <h2>Annual Net Operating Income by Property</h2>
                <p className="chart-subtitle">
                  {showAfterDebt
                    ? 'Net profit after debt service (mortgage payments deducted)'
                    : 'Net profit before debt service, insurance, taxes, cap ex, etc. — ONLY income minus operating expenses'}
                </p>
              </div>
              <div className="annual-chart-actions">
                <div className="chart-debt-toggle">
                  <button
                    className={`chart-debt-btn${!showAfterDebt ? ' chart-debt-btn--active' : ''}`}
                    onClick={() => setShowAfterDebt(false)}
                  >Before Debt</button>
                  <button
                    className={`chart-debt-btn${showAfterDebt ? ' chart-debt-btn--active' : ''}`}
                    onClick={() => setShowAfterDebt(true)}
                  >After Debt</button>
                </div>
                <div className="chart-theme-picker">
                  {['dark','green','slate','navy','warm','light','sky','white'].map(t => (
                    <button
                      key={t}
                      className={`chart-theme-swatch chart-theme-swatch--${t}${chartTheme === t ? ' chart-theme-swatch--active' : ''}`}
                      onClick={() => setChartTheme(t)}
                      title={t.charAt(0).toUpperCase() + t.slice(1)}
                    />
                  ))}
                </div>
                <div className="annual-chart-period">{dashboardData.latestPeriodLabel}</div>
                <div className="annual-range-filter">
                  <span>Year range</span>
                  <div
                    className="annual-range-slider"
                    style={{
                      '--range-start': `${rangeStartPercent}%`,
                      '--range-end': `${rangeEndPercent}%`,
                    }}
                  >
                    <div className="annual-range-track" />
                    <input
                      className="annual-range-input annual-range-input--start"
                      type="range"
                      min="0"
                      max={maxYearIndex}
                      value={activeStartYearIndex}
                      disabled={maxYearIndex <= minYearSpan}
                      onChange={handleStartYearChange}
                      aria-label="Annual chart start year"
                    />
                    <input
                      className="annual-range-input annual-range-input--end"
                      type="range"
                      min="0"
                      max={maxYearIndex}
                      value={activeEndYearIndex}
                      disabled={maxYearIndex <= minYearSpan}
                      onChange={handleEndYearChange}
                      aria-label="Annual chart end year"
                    />
                  </div>
                  <strong>{yearRangeLabel}</strong>
                </div>
              </div>
            </div>

            <div className="annual-chart-summary">
              <div>
                <span>Viewing</span>
                <strong>{annualNetIncomeMeta.timeframeLabel}</strong>
                <small>{annualNetIncomeMeta.coverageLabel}</small>
              </div>
              <div>
                <span>Top earner</span>
                <strong>{annualNetIncomeMeta.topEarner}</strong>
                <small>{annualNetIncomeMeta.topEarnerTotal}</small>
              </div>
              <div>
                <span>Most reliable</span>
                <strong>{annualNetIncomeMeta.mostConsistent}</strong>
                <small>{annualNetIncomeMeta.consistencyDetail}</small>
              </div>
              <div>
                <span>Total Net Profit</span>
                <strong>{annualNetIncomeMeta.totalTracked}</strong>
                <small>{yearRangeLabel}</small>
              </div>
            </div>

            <div className="annual-chart-frame">
              {hasAnnualNetIncome ? (
                <Line data={filteredAnnualNetIncomeChart} options={annualNetIncomeOptions} />
              ) : (
                <div className="annual-chart-empty">Need at least two years for a line chart.</div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
